import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUp, Camera, ImagePlus, Loader2, ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Reveal } from "./Reveal";
import { useCart } from "../context/CartContext";
import { API, getSessionId, imageToJpeg, streamChat, streamSSE } from "../lib/api";
import { Markdown } from "./Markdown";
import { DiagRecs } from "./DiagRecs";

const STAGES = [
  ["Plántula / Esqueje", ["grow"]],
  ["Crecimiento", ["grow"]],
  ["Floración S1–S2", ["a-bloom"]],
  ["Floración S3–S4", ["b-bloom", "pk-065", "bongo-bloom"]],
  ["Floración S5–S7", ["b-bloom", "pk-065", "bongo-bloom", "compact"]],
  ["Lavado (S8)", []],
];
const SUBSTRATES = ["Tierra viva", "Fibra de coco", "Hidroponía DWC", "Aeroponía"];
const SYMPTOMS = ["Hojas amarillas", "Puntas quemadas", "Manchas óxido", "Crecimiento lento", "Entrenudos largos", "Ninguno · Prevención"];

const Chips = ({ label, options, value, onPick, multi, testid }) => (
  <div data-testid={testid}>
    <p className="eyebrow !text-[#E3EADF] mb-3">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = multi ? value.includes(o) : value === o;
        return (
          <button key={o} type="button" onClick={() => onPick(o)} data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`rounded-full px-4 h-9 text-sm border transition-colors duration-300 ${
              on ? "bg-[#8CD05A] border-[#8CD05A] text-[#0E2A1C] font-semibold" : "border-[#8CD05A]/25 text-[#F6EEDC] hover:border-[#F2B441] hover:text-[#F2B441]"}`}>
            {o}
          </button>
        );
      })}
    </div>
  </div>
);

export const Assistant = ({ products }) => {
  const { add, setOpen } = useCart();
  const [stage, setStage] = useState(STAGES[1][0]);
  const [substrate, setSubstrate] = useState(SUBSTRATES[1]);
  const [symptoms, setSymptoms] = useState([]);
  const [genetic, setGenetic] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recs, setRecs] = useState([]);
  const sid = useRef(null);
  const box = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    sid.current = getSessionId();
    if (!API) return;
    axios.get(`${API}/chat/${sid.current}`).then((r) => {
      const h = Array.isArray(r.data) ? r.data : [];
      setMsgs(h.map((m, i) => (m.role === "assistant" && h[i - 1]?.content?.startsWith("[Foto") ? { ...m, diag: true } : m)));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [msgs]);

  const toggleSym = (s) => setSymptoms((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const appendToken = (t) =>
    setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t }; return c; });
  const failReply = () =>
    setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "El asistente no está disponible ahora mismo. Inténtalo de nuevo en unos segundos." }; return c; });

  const send = async (text) => {
    if (!text.trim() || busy || !API) return;
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      await streamChat(sid.current, text, appendToken);
    } catch (e) {
      failReply();
    }
    setBusy(false);
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || busy || !API) return;
    if (!f.type.startsWith("image/")) { toast.error("El archivo debe ser una imagen."); return; }
    let dataUrl;
    try {
      dataUrl = await imageToJpeg(f);
    } catch {
      toast.error("No se pudo leer la imagen. Prueba con una foto JPG o PNG.");
      return;
    }
    const g = genetic.trim() ? `Genética: ${genetic.trim()}. ` : "";
    const note = `${g}Etapa: ${stage}. Sustrato: ${substrate}.${input.trim() ? ` Nota: ${input.trim()}` : ""}`;
    setInput("");
    setRecs([]);
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: "Foto enviada para diagnóstico", image: dataUrl }, { role: "assistant", content: "", diag: true }]);
    document.getElementById("ai-console")?.scrollIntoView({ behavior: "smooth", block: "center" });
    try {
      await streamSSE("/diagnose", { session_id: sid.current, image_base64: dataUrl.split(",")[1], note }, appendToken);
    } catch {
      failReply();
    }
    setBusy(false);
  };

  const generate = () => {
    const ids = STAGES.find(([s]) => s === stage)?.[1] ?? [];
    setRecs(products.filter((p) => ids.includes(p.id)));
    const g = genetic.trim() ? `Genética: ${genetic.trim()}. ` : "";
    send(`${g}Etapa: ${stage}. Sustrato: ${substrate}. Síntomas: ${symptoms.length ? symptoms.join(", ") : "ninguno"}. Genera mi plan de fertilización Jungle Nutrient.`);
  };

  const addRecs = () => { recs.forEach((p) => add(p, 1, true)); setOpen(true); };

  return (
    <section id="asistente" data-testid="ai-assistant-section" className="relative py-24 md:py-36 bg-blue-leaves border-y-4 border-[#F2B441]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-5 flex flex-col gap-8">
          <Reveal>
            <p className="eyebrow mb-6 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Nutricionista neural</p>
            <h2 className="font-serif-d font-black text-5xl md:text-6xl leading-[0.92] tracking-tight">
              Tu planta habla.<br /><span className="italic font-semibold text-[#F2B441]">La IA traduce.</span>
            </h2>
            <p className="text-[#E3EADF] mt-6 leading-relaxed">Nuestra IA conoce la mayoría de genéticas de cannabis. Indica tu variedad, etapa, sustrato y síntomas y recibe un plan con dosis en ml/L y objetivos de pH y EC.</p>
          </Reveal>
          <div>
            <p className="eyebrow !text-[#E3EADF] mb-3">00 · Genética (opcional)</p>
            <input data-testid="ai-assistant-genetic-input" value={genetic} onChange={(e) => setGenetic(e.target.value)} maxLength={80}
              placeholder="Ej.: Gorilla Glue #4, Amnesia Haze, Critical auto…"
              className="w-full bg-transparent border-b border-[#8CD05A]/30 focus:border-[#F2B441] h-12 text-lg font-serif-d placeholder:text-[#93A88E] outline-none transition-colors" />
          </div>
          <Chips label="01 · Etapa" testid="ai-assistant-stage-select" options={STAGES.map(([s]) => s)} value={stage} onPick={setStage} />
          <Chips label="02 · Sustrato" testid="ai-assistant-substrate-select" options={SUBSTRATES} value={substrate} onPick={setSubstrate} />
          <Chips label="03 · Síntomas" testid="ai-assistant-symptoms-select" options={SYMPTOMS} value={symptoms} onPick={toggleSym} multi />
          <button data-testid="ai-assistant-generate-button" onClick={generate} disabled={busy}
            className="self-start inline-flex items-center gap-3 rounded-full bg-[#8CD05A] text-[#0E2A1C] px-7 h-14 font-semibold hover:bg-[#F2B441] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar plan con IA
          </button>
          <button type="button" data-testid="ai-photo-diagnose-button" onClick={() => fileRef.current?.click()} disabled={busy}
            className="group text-left rounded-[10px] border-2 border-dashed border-[#F2B441]/60 bg-[#0E2A1C]/70 backdrop-blur-md p-5 flex items-center gap-5 hover:border-[#F2B441] hover:bg-[#0E2A1C]/90 transition-colors disabled:opacity-60">
            <span className="w-14 h-14 shrink-0 rounded-full bg-[#F2B441] text-[#0E2A1C] grid place-items-center transition-transform duration-500 group-hover:scale-110"><Camera className="w-6 h-6" /></span>
            <span>
              <span className="block font-label text-xl text-[#F6EEDC]">Diagnóstico por foto</span>
              <span className="block text-sm text-[#E3EADF] mt-1">Sube o haz una foto de la planta y la IA identifica el problema y te dice cómo corregirlo.</span>
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="ai-photo-input" />
        </div>

        <div id="ai-console" className="lg:col-span-7 flex flex-col rounded-[10px] overflow-hidden border border-[#F2B441]/40 bg-[#0E2A1C]/95 backdrop-blur-md min-h-[560px] max-h-[720px] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.7)]">
          <div className="flex items-center justify-between px-5 h-14 border-b border-[#8CD05A]/15">
            <span className="flex items-center gap-2 font-mono-d text-[11px] tracking-[0.2em] text-[#E3EADF]">
              <span className={`w-2 h-2 rounded-full ${busy ? "bg-[#F2B441] animate-pulse" : "bg-[#8CD05A]"}`} /> JUNGLE IA · CONSOLA
            </span>
            <span className="font-mono-d text-[10px] text-[#E3EADF]">{busy ? "PROCESANDO…" : "EN LÍNEA"}</span>
          </div>
          <div ref={box} data-lenis-prevent data-testid="ai-chat-messages" className="flex-1 overflow-y-auto p-5 md:p-7 space-y-5">
            {msgs.length === 0 && (
              <div className="h-full grid place-items-center text-center py-16">
                <div>
                  <p className="font-serif-d italic font-semibold text-3xl text-[#F2B441]">“¿Qué necesita tu cultivo hoy?”</p>
                  <p className="text-[#E3EADF] text-sm mt-3">Configura los parámetros, escribe tu pregunta o envía una foto de tu planta.</p>
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={m.role === "user" ? "ml-auto max-w-[85%] bg-[#1A4430] border border-[#8CD05A]/20 px-4 py-3 text-sm" : "max-w-full"}>
                {m.role === "user" ? (
                  <>
                    {m.image && <img src={m.image} alt="Foto de la planta" data-testid="ai-chat-photo" className="mb-2 max-h-56 w-auto rounded-[4px] border border-[#F2B441]/40" />}
                    {m.content}
                  </>
                ) : (
                  m.content ? <><Markdown text={m.content} />{m.diag && !(busy && i === msgs.length - 1) && <DiagRecs text={m.content} products={products} />}</> : <span className="inline-flex gap-1 py-2">{[0, 1, 2].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#8CD05A] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}</span>
                )}
              </motion.div>
            ))}
            {recs.length > 0 && !busy && (
              <div data-testid="ai-recommendations" className="border border-[#F2B441]/35 bg-[#F2B441]/5 p-4 flex flex-wrap items-center gap-4 justify-between">
                <div className="flex items-center gap-3">
                  {recs.map((p) => <img key={p.id} src={p.image} alt={p.name} className="w-12 h-14 object-cover border border-[#F2B441]/30" />)}
                  <p className="text-sm">Recomendado: <span className="text-[#F2B441]">{recs.map((r) => r.name).join(" + ")}</span></p>
                </div>
                <button data-testid="ai-add-recommended-button" onClick={addRecs} className="inline-flex items-center gap-2 rounded-full bg-[#F2B441] text-[#0E2A1C] px-5 h-10 text-sm font-semibold hover:bg-[#F6EEDC] transition-colors">
                  <ShoppingBag className="w-4 h-4" /> Añadir al carrito
                </button>
              </div>
            )}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(input); setInput(""); }} className="flex items-center gap-2 p-3 border-t border-[#8CD05A]/15">
            <input data-testid="ai-assistant-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Pregunta sobre dosis, carencias, pH…"
              className="flex-1 bg-transparent h-12 px-3 text-sm placeholder:text-[#93A88E] outline-none" />
            <button type="button" data-testid="ai-chat-photo-button" onClick={() => fileRef.current?.click()} disabled={busy} aria-label="Enviar foto"
              className="w-12 h-12 rounded-full border border-[#F2B441]/50 text-[#F2B441] grid place-items-center disabled:opacity-40 hover:bg-[#F2B441] hover:text-[#0E2A1C] transition-colors">
              <ImagePlus className="w-4 h-4" />
            </button>
            <button data-testid="ai-assistant-chat-send" disabled={busy || !input.trim()} className="w-12 h-12 rounded-full bg-[#8CD05A] text-[#0E2A1C] grid place-items-center disabled:opacity-40 hover:bg-[#F2B441] transition-colors">
              <ArrowUp className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
